import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { apiFetch, clearSessionToken, getSessionToken } from "../lib/session";

export type Plan = "FREE" | "PRO" | "EXPORT";

export type Account = {
  id: string;
  whatsapp: string;
  displayName: string;
  email: string;
  status: string;
  plan: Plan;
  planLabel: string;
  entitlements: {
    dashboardAccess: boolean;
    maxWebsites: number;
    maxProducts: number;
    sectionBuilder: boolean;
    analytics: boolean;
    customDomain: boolean;
    exportTools: boolean;
  };
  site: null | {
    websiteId: string;
    slug: string;
    previewToken: string;
    status: string;
    revision: number;
    businessName: string;
    lastUpdatedBy: string;
    lastUpdatedAt: string | null;
  };
};

type AccountContextValue = {
  account: Account | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!getSessionToken()) {
      setAccount(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await apiFetch<{ account: Account }>("/api/me");
      setAccount(result.account);
    } catch {
      clearSessionToken();
      setAccount(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await apiFetch("/api/logout", { method: "POST" }); } catch { /* ignore */ }
    clearSessionToken();
    setAccount(null);
  };

  useEffect(() => { void refresh(); }, []);

  const value = useMemo(() => ({ account, loading, refresh, logout }), [account, loading]);
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const value = useContext(AccountContext);
  if (!value) throw new Error("useAccount must be used inside AccountProvider");
  return value;
}
